<template>
  <form @submit.prevent="handleSubmit" class="side-wrapper side-wrapper-flex">
    <!-- Header -->
    <div class="side-header">
      <div class="side-wrapper-subtitle">{{ generalStore.viewHeader }}</div>
      <h1 v-if="form.id">{{ $gettext("Edit group") }} '{{ form.name }}'</h1>
      <h1 v-else>{{ $gettext("Create group") }}</h1>
    </div>

    <div class="side-body">
      <!-- Name -->
      <NiceInput :title="$gettext('Name')" v-model="form.name" type="text" :required="true" placeholder="Regular" />

      <!-- Color -->
      <!-- <NiceColor :title="$gettext('Color')" v-model="form.color" /> -->
      <div class="nice-component nice-input">
        <div class="nice-component-header">
          <div class="nice-component-title">{{ $gettext("Color") }}</div>
        </div>
        <div class="input-group">
          <input type="color" v-model="form.color" />
        </div>
      </div>

      <!-- Allow entry until -->
      <NiceDate :title="$gettext('Allow entry until')" v-model="form.allow_entry_until" :required="true" />

      <!-- Enable guest limit -->
      <NiceSwitch :title="$gettext('Enable guest limit')" v-model="form.enable_guest_limit" />

      <!-- Guest limit -->
      <NiceInput :title="$gettext('Guest limit')" v-model="form.guest_limit" v-if="form.enable_guest_limit" type="number" placeholder="0" />

      <!-- Require email -->
      <NiceSwitch :title="$gettext('Require email')" v-model="form.require_email" />

      <!-- Require phone number -->
      <NiceSwitch :title="$gettext('Require phone number')" v-model="form.require_phone_number" />

      <!-- Require photo -->
      <NiceSwitch :title="$gettext('Require photo')" v-model="form.require_photo" />

      <!-- Require company -->
      <NiceSwitch :title="$gettext('Require company')" v-model="form.require_company" />

      <!-- Require partner -->
      <NiceSwitch :title="$gettext('Require partner')" v-if="modules.PARTNERS" v-model="form.require_partner" />

      <!-- Allow plus one -->
      <NiceSwitch :title="$gettext('Allow plus one')" v-model="form.allow_plus_one" v-if="modules.PLUS_ONES" />

      <!-- Plus one limit per guest -->
      <NiceInput
        :title="$gettext('Plus one limit per guest')"
        v-if="form.allow_plus_one && modules.PLUS_ONES"
        v-model="form.plus_one_limit_per_guest"
        type="number"
        placeholder="0"
        :required="true"
      />

      <div v-if="modules.TICKETING_INTEGRATION && event.ticketing_integration_event_id">
        <!-- Enable ticketing integration -->
        <NiceSwitch :title="$gettext('Enable ticketing integration')" v-model="form.enable_ticketing_integration" />

        <div v-if="form.enable_ticketing_integration">
          <!-- Ticketing event -->
          <NiceDropdown
            :title="$gettext('Ticketing ticket type')"
            v-model="form.ticketingTicketType"
            :searchFunction="searchTicketingTicketTypes"
            @change="onTicketingTicketTypeSelected"
            nullable
            valueName="name"
          ></NiceDropdown>

          <!-- Ticketing integration ticket type ID -->
          <NiceInput
            :title="$gettext('Ticketing integration ticket type ID')"
            v-model="form.ticketing_integration_ticket_type_id"
            type="text"
            :required="true"
            :disabled="true"
            placeholder="000"
          />
        </div>
      </div>

      <!-- Override invite content -->
      <NiceSwitch :title="$gettext('Override invite content')" v-model="form.override_invite_content" />

      <div v-if="form.override_invite_content">
        <!-- Invite email content -->
        <NiceTextarea :title="$gettext('Invite email content')" v-model="form.invite_email_content" />

        <!-- Invite form intro content -->
        <NiceTextarea :title="$gettext('Invite form intro content')" v-model="form.invite_form_intro_content" />

        <!-- Invite submit confirmation content -->
        <NiceTextarea :title="$gettext('Invite submit confirmation content')" v-model="form.invite_submit_confirmation_content" />
      </div>

      <!-- Use A4 template -->
      <div v-if="event.ticket_use_a4_template">
        <!-- Override ticket content -->
        <NiceSwitch :title="$gettext('Override ticket content')" v-model="form.override_ticket_content" />

        <div v-if="form.override_ticket_content">
          <!-- Ticket header photo -->
          <NiceUpload
            :title="$gettext('Ticket header photo')"
            v-model="form.ticket_header_image"
            accept="image/png, image/jpeg"
            :loading="loading"
            @change="onFileChange($event, 'ticket_header_image')"
          />

          <!-- Ticket top left content -->
          <NiceTextarea :title="$gettext('Ticket top left content')" v-model="form.ticket_top_left_content" />

          <!-- Ticket bottom left content -->
          <NiceTextarea :title="$gettext('Ticket bottom left content')" v-model="form.ticket_bottom_left_content" />

          <!-- Ticket bottom right content -->
          <NiceTextarea :title="$gettext('Ticket bottom right content')" v-model="form.ticket_bottom_right_content" />

          <!-- Ticket email content -->
          <NiceTextarea :title="$gettext('Ticket email content')" v-model="form.ticket_email_content" />
        </div>
      </div>

      <!-- <pre>{{ form }}</pre> -->
    </div>

    <!-- Footer -->
    <div class="side-footer">
      <NiceActions @cancel="cancel" :loading="loading" :showDelete="!!form.id" @delete="handleDelete" />
    </div>
  </form>
</template>

<script setup>
import { defineProps, onMounted, defineEmits, ref, computed } from "vue";

import { useGettext } from "vue3-gettext";
const { $gettext } = useGettext();

import { useRouter } from "vue-router";
const router = useRouter();

import { useGeneralStore } from "@/store/general.store";
const generalStore = useGeneralStore();
const event = ref(generalStore.event);
const modules = computed(() => generalStore.modules);

import ApiService from "@/services/api/api.service";
import CommonService from "@/services/common.service";
import ErrorService from "@/services/error.service";
import { clone } from "@/plugins/query";

const organizationId = router.currentRoute.value.params.organizationId;
const eventId = router.currentRoute.value.params.eventId;

const emit = defineEmits(["cancel", "success"]);
const props = defineProps({
  data: {
    type: Object,
    required: false,
  },
});
const form = ref({
  name: null,
  color: CommonService.generateRandomColor(),
  allow_plus_one: false,
  allow_entry_until: event.value.end_time,
  enable_guest_limit: false,
  guest_limit: 100,
  plus_one_limit_per_guest: 0,
  require_email: false,
  require_phone_number: false,
  require_photo: false,
  require_company: false,
  require_partner: false,
  enable_ticketing_integration: false,
  ticketing_integration_ticket_type_id: null,

  override_invite_content: false,
  invite_email_content: null,
  invite_form_intro_content: null,
  invite_submit_confirmation_content: null,
  ticket_email_content: null,
  override_ticket_content: false,
  ticket_header_image: null,
  ticket_top_left_content: null,
  ticket_bottom_left_content: null,
  ticket_bottom_right_content: null,
});
const loading = ref(false);

async function searchTicketingTicketTypes(keyword) {
  return ApiService.internal.getTicketingEventTicketTypes(organizationId, eventId, { search: keyword });
}

function onTicketingTicketTypeSelected(ticketType) {
  if (!ticketType) ticketType = {};
  if (!form.value.id) form.value.name = ticketType.name || null;
  form.value.ticketing_integration_ticket_type_id = ticketType.id || null;
  form.value.allow_entry_until = ticketType.valid_to || event.value.end_time || null;
}

async function handleSubmit() {
  loading.value = true;
  try {
    if (form.value.id) {
      form.value = await ApiService.groups.update(organizationId, eventId, form.value);
      CommonService.success($gettext("Group was updated"));
    } else {
      form.value = await ApiService.groups.create(organizationId, eventId, form.value);
      CommonService.success($gettext("Group was created"));
    }
    emit("success");
  } catch (error) {
    ErrorService.showError(error);
  } finally {
    loading.value = false;
  }
}

async function handleDelete() {
  loading.value = true;
  try {
    form.value = await ApiService.groups.delete(organizationId, eventId, form.value.id);
    CommonService.success($gettext("Group was deleted"));
    emit("success");
  } catch (error) {
    ErrorService.showError(error);
  } finally {
    loading.value = false;
  }
}

async function onFileChange(file, field) {
  if (!file) return (form.value[field] = null);
  if (!file[0]) return (form.value[field] = null);
  if (!["image/jpeg", "image/png"].includes(file[0].type)) {
    CommonService.error($gettext("Wrong file format. Only .jpg and .png files are allowed."));
    return (form.value[field] = null);
  }

  loading.value = true;
  try {
    const response = await ApiService.storage.uploadPhoto(file[0], CommonService.photoCompressionTypes.COVER);
    form.value[field] = response.file;
  } catch (error) {
    ErrorService.showError(error);
    form.value[field] = null;
  } finally {
    loading.value = false;
  }
}

function cancel() {
  emit("cancel");
}

onMounted(async () => {
  const groupId = router.currentRoute.value.query.id;
  if (groupId == "new") return;
  if (props.data) return (form.value = clone(props.data));

  try {
    form.value = await ApiService.groups.get(organizationId, eventId, groupId);
  } catch (e) {
    emit("cancel");
  }
});
</script>
